import { useEffect } from 'react';

import { useRouter } from 'next/router';

import ThumbnailImage from 'src/components/thumbnail/components/ThumbnailImage';
import { ThumbnailTypes } from 'src/components/thumbnail/types';
import { fetchTopSearch } from 'src/services/apiRequests/TopSearchService';
import { TopSearchType } from 'src/services/responseTypes/topSearchResponse';
import { UsersPreferenceData } from 'src/store/interface';
import { mixpanelObject } from 'src/utils/analytics';
import { thumbnailPropertySetter } from 'src/utils/analytics/mixpanel/propertySetter';
import {
  generateCardsUrl,
  navigationHandler
} from 'src/utils/cardsUrlGenerator';
import { parseJsonObject } from 'src/utils/common';
import { TAG_NAME } from 'src/utils/constants/TagNameConstants';
import { getImageURL } from 'src/utils/ImageHelper/ImageURLGenerator';

import { useAppStore } from 'src/hooks/useAppStore';
import {
  MIXPANEL_CAROUSAL_NAME,
  MIXPANEL_CONTENT_TYPE,
  MIXPANEL_HORIZONTAL_INDEX,
  MIXPANEL_QUERY_ID,
  MIXPANEL_VERTICAL_INDEX
} from '../../../utils/analytics/mixpanel/mixpanelConstants';
import { searchResultClickedEvent } from '../../../utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  thumbnailImageStyle,
  topSearchContainer,
  topSearchWrapper
} from './TopSearchStyle';

/* Get content appropriateAge */
export const getContentAppropriateAge = (settings: UsersPreferenceData) => {
  const parsedParentalControlV2 = parseJsonObject(settings.parental_control_v2);
  return parsedParentalControlV2 && parsedParentalControlV2.age_rating;
};

type TopSearchPropsType = {
  hideMenu: boolean;
  onBlur: () => void;
};

const TopSearch = ({ hideMenu, onBlur }: TopSearchPropsType) => {
  const {
    usersLocationData,
    usersPreferenceData,
    topSearch,
    setTopSearch,
    appConfig,
    translations,
    isUserLoggedIn
  } = useAppStore();
  const router = useRouter();
  const translationData = translations?.common;
  const { WebSearch_Title_TopSearches_Text } = translationData;
  const deviceWidth = appConfig?.deviceWidth || 0;
  const countryCode = usersLocationData?.country || '';
  const contentLanguage = usersPreferenceData?.content_language || '';
  const state = usersLocationData?.state || '';
  const city = usersLocationData?.city || '';

  const contentAppropriateAge =
    isUserLoggedIn && usersPreferenceData
      ? getContentAppropriateAge(usersPreferenceData)
      : false;

  const getTopSearchData = async () => {
    if (topSearch?.length === 0) {
      try {
        const topSearchData = await fetchTopSearch(
          countryCode,
          contentLanguage,
          contentAppropriateAge,
          state,
          city
        );
        setTopSearch(topSearchData);
      } catch (err) {
        console.error(err);
      }
    }
  };

  useEffect(() => {
    getTopSearchData();
  }, []);

  return (
    <div css={topSearchContainer} data-testid="top-search-container">
      {topSearch?.length > 0 ? (
        <div>
          <h3>{WebSearch_Title_TopSearches_Text}</h3>
          <div css={topSearchWrapper}>
            {topSearch.map((item: TopSearchType, index: number) => {
              const formattedItem = {
                id: item.id,
                title: item.title,
                assetType: item.asset_type,
                image: item.image
              };
              const imageUrl = getImageURL({
                deviceWidth,
                tagName: TAG_NAME.PORTRAIT_SMALL,
                item: formattedItem
              });

              let slugData = generateCardsUrl(item) ?? '';
              slugData = slugData.startsWith('/') ? slugData : `/${slugData}`;

              const searchResultClickMixpanelEvent = (index: number) => {
                let cardCountTopSearch = 3;
                if (deviceWidth >= 600 && deviceWidth <= 1200) {
                  cardCountTopSearch = 5;
                }
                const verticalIndex =
                  index % cardCountTopSearch || cardCountTopSearch;
                mixpanelObject.setProperty(
                  MIXPANEL_VERTICAL_INDEX,
                  verticalIndex
                );
                mixpanelObject.setProperty(
                  MIXPANEL_HORIZONTAL_INDEX,
                  Math.ceil(index / cardCountTopSearch)
                );
                mixpanelObject.setProperty(
                  MIXPANEL_CONTENT_TYPE,
                  item?.contentType
                );

                mixpanelObject.setProperty(
                  MIXPANEL_CAROUSAL_NAME,
                  WebSearch_Title_TopSearches_Text
                );
                mixpanelObject.setProperty(
                  MIXPANEL_QUERY_ID,
                  WebSearch_Title_TopSearches_Text
                );
                searchResultClickedEvent();
              };

              const watchCallback = () => {
                onBlur();
                if (slugData) {
                  window.storeMixPanelMetricsInSS('CLICKED_EVT', Date.now());
                  thumbnailPropertySetter({ item: { ...item, imageUrl } });
                  searchResultClickMixpanelEvent(index + 1);
                  navigationHandler(slugData, (slugData) =>
                    router.push(slugData, slugData)
                  );
                }
              };

              return (
                <div
                  key={index}
                  css={thumbnailImageStyle(hideMenu)}
                  data-testid="top-search-item">
                  <ThumbnailImage
                    imageUrl={imageUrl}
                    assetType={item.asset_type}
                    slug={slugData || ''}
                    businessType={item.business_type}
                    billingType={item.billing_type}
                    onClickHandler={watchCallback}
                    thumbnailType={ThumbnailTypes.vertical}
                    title={item.title}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TopSearch;
